import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Zap, Target, TrendingUp, CheckCircle, Rocket, Phone, Code, BarChart3, Calculator, MapPin, Users, Sparkles, BookOpen, Calendar, Globe, Award, Shield } from 'lucide-react';
import SEOHelmet from '../components/SEOHelmet';
import ThreeDHeroSlider from '../components/ThreeDHeroSlider';
import { generateLocalBusinessSchema, generateBreadcrumbSchema, generateWebSiteSchema } from '../utils/schemaGenerator';
import { cities } from '../data/cities';

const Home = () => {
  const hubRef = useRef(null);
  const proofRef = useRef(null);
  const ctaRef = useRef(null);
  const citiesRef = useRef(null);
  const blogRef = useRef(null);

  const hubInView = useInView(hubRef, { once: true });
  const proofInView = useInView(proofRef, { once: true });
  const ctaInView = useInView(ctaRef, { once: true });
  const citiesInView = useInView(citiesRef, { once: true });
  const blogInView = useInView(blogRef, { once: true });

  const hubCards = [
    {
      icon: <Code size={40} />,
      title: 'Création & Refonte',
      description: 'Des plateformes performantes, codées sur-mesure (React) pour la vitesse et la conversion.',
      link: '/services',
      gradient: 'from-[#6c5ce7] to-[#a29bfe]'
    },
    {
      icon: <BarChart3 size={40} />,
      title: 'Acquisition & Trafic',
      description: 'SEO Local, Google Ads... Nous faisons venir les clients qualifiés sur votre site.',
      link: '/marketing/seo-local',
      gradient: 'from-[#00cec9] to-[#81ecec]'
    },
    {
      icon: <Calculator size={40} />,
      title: 'Stratégie & Budget',
      description: 'Combien coûte votre ambition ? Estimez votre projet en 2 minutes chrono.',
      link: '/simulateur',
      gradient: 'from-[#fd79a8] to-[#e84393]'
    }
  ];

  const proofPoints = [
    {
      title: 'Pas de WordPress lent.',
      description: 'Nous codons en React pour une vitesse qui plaît à Google et aux utilisateurs.'
    },
    {
      title: 'Ancrage Local Yvelines.',
      description: 'Nous connaissons votre marché. On se rencontre en vrai.'
    },
    {
      title: 'Vision Business, pas juste Tech.',
      description: 'Votre site est un investissement qui doit rapporter, pas une dépense.'
    }
  ];

  return (
    <>
      <SEOHelmet
        title="Agence Web Yvelines (78) | Création Site Internet & Marketing Digital"
        description="Agence web experte dans les Yvelines (78). Création de sites internet performants, SEO local, Google Ads. Devis gratuit sous 24h. Proximité & Résultats."
        keywords="agence web yvelines, création site internet 78, seo local yvelines, agence web bois d'arcy, agence digitale versailles"
        canonicalUrl="/"
        schemaData={[
          generateWebSiteSchema(),
          generateLocalBusinessSchema(),
          generateBreadcrumbSchema([{ name: 'Accueil', url: 'https://webyoufirst.fr/' }])
        ]}
      />

      {/* ========================================= */}
      {/* 3D PARALLAX HERO SLIDER */}
      {/* ========================================= */}
      <ThreeDHeroSlider />

      {/* ========================================= */}
      {/* EDITO SEO : L'Agence Web de Référence Yvelines */}
      {/* ========================================= */}
      <section className="py-24 relative overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.01)' }}>
        {/* Abstract Background Noise & Orbs */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[150px] opacity-10" style={{ background: '#6c5ce7' }}></div>

        <div className="max-w-[1200px] mx-auto px-6 lg:px-20 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} className="prose prose-lg prose-invert text-white/80 max-w-none">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-8 leading-tight">
              Votre Agence Web dans les Yvelines (78) : <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00cec9] to-[#6c5ce7]">L'Expertise au Service de votre Croissance</span>
            </h2>

            <p className="lead text-2xl text-white/95 font-medium mb-10 border-l-4 border-[#00cec9] pl-6 py-2 bg-[#00cec9]/5 leading-relaxed">
              Dans un monde hyper-connecté où la compétitivité numérique définit le succès de demain, confier sa présence en ligne à une "petite agence généraliste" n'est plus une option. L'agence <strong>WEB YOU FIRST</strong>, située au cœur des Yvelines, s'impose comme l'architecte digital des PME, artisans, commerçants et industries d'Île-de-France.
            </p>

            <div className="grid md:grid-cols-2 gap-12 mt-12">
              <div>
                <h3 className="flex items-center text-2xl font-bold text-white mb-6">
                  <Award className="text-[#00cec9] mr-3" size={28} />
                  Pourquoi choisir une agence experte de proximité ?
                </h3>
                <p className="mb-6 leading-relaxed text-lg">
                  La création d'un site internet professionnel n'est pas un concept purement esthétique ; c'est une ingénierie complexe. La vitesse de chargement de vos pages, exigée par le nouvel algorithme de Google (Core Web Vitals), nécessite une maîtrise absolue du code. C'est pourquoi notre agence web a pris la décision radicale de s'éloigner des CMS vieillissants, lents et vulnérables (comme WordPress) pour se spécialiser dans les technologies React.js.
                </p>
                <p className="mb-6 leading-relaxed text-lg">
                  Ce parti-pris technologique élitiste nous permet de déployer, pour votre entreprise, des plateformes digitales totalement inviolables et extrêmement véloces. Avoir un site internet réactif sur smartphone n'est plus suffisant : le vôtre s'affichera instantanément, réduisant votre taux de rebond au néant et impressionnant chaque prospect dès la première seconde. De plus, notre fort ancrage géographique (Versailles, SQY, Trappes, Plaisir) autorise des rencontres physiques régulières. Nous aimons comprendre la réalité du terrain.
                </p>
              </div>

              <div>
                <h3 className="flex items-center text-2xl font-bold text-white mb-6">
                  <Globe className="text-[#6c5ce7] mr-3" size={28} />
                  Référencement Naturel & Acquisition de Trafic
                </h3>
                <p className="mb-6 leading-relaxed text-lg">
                  Posséder le plus beau site web des Yvelines est inutile s'il demeure invisible dans le désert numérique. Contrairement aux agences traditionnelles, <strong>WEB YOU FIRST intègre une stratégie de Référencement Naturel (SEO) au cœur même de son processus de création</strong>. Avant même d'écrire la première ligne de code, nous auditons l'intention de recherche de vos futurs clients pour bâtir une arborescence (architecture de pages) qui capturera un trafic Google massif.
                </p>
                <p className="mb-6 leading-relaxed text-lg">
                  Pour les artisans et les commerces, notre cheval de bataille est le SEO Local. Nous paramétrons et optimisons votre fiche Google Business Profile, nous déployons un maillage géographique rigoureux, et nous gérons l'acquisition de vos avis. Résultat : quand un cherche un service de plomberie, de mécanique ou de conseil juridique dans votre agglomération, c'est votre devanture digitale qui accapare le "Top 3" de la carte Google.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========================================= */}
      {/* SECTION 1 : HUB D'EXPERTISE (Maillage) */}
      {/* ========================================= */}
      <section ref={hubRef} className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0" style={{ background: 'rgba(255, 255, 255, 0.01)' }}></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[150px] opacity-10"
          style={{ background: 'radial-gradient(circle, #6c5ce7 0%, transparent 70%)' }}></div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={hubInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Notre arsenal digital pour
              <span className="block bg-gradient-to-r from-[#6c5ce7] to-[#00cec9] bg-clip-text text-transparent">votre croissance</span>
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Trois piliers. Un objectif : transformer votre présence en ligne en machine à générer du business.
            </p>
          </motion.div>

          {/* Hub Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {hubCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={hubInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.15 }}
              >
                <Link to={card.link} className="block h-full">
                  <div className="h-full p-8 rounded-2xl transition-all duration-500 hover:scale-[1.03] group cursor-pointer relative overflow-hidden"
                    style={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.06)'
                    }}
                  >
                    {/* Hover Glow Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 transition-all duration-500`}></div>

                    {/* Top Glow Line */}
                    <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${card.gradient} opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>

                    <div className="relative z-10">
                      {/* Icon with Glow */}
                      <div className={`inline-flex p-4 rounded-xl mb-6 bg-gradient-to-br ${card.gradient} relative`}
                        style={{ boxShadow: `0 0 30px rgba(108, 92, 231, 0.3)` }}
                      >
                        <div className="text-white">{card.icon}</div>
                      </div>

                      <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#00cec9] transition-colors">
                        {card.title}
                      </h3>

                      <p className="mb-6 text-lg" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {card.description}
                      </p>

                      <div className="inline-flex items-center text-[#00cec9] font-medium group-hover:translate-x-2 transition-transform duration-300">
                        <span className="mr-2">Découvrir</span>
                        <ArrowRight size={18} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================= */}
      {/* SECTION 2 : LA PREUVE PAR L'APPROCHE */}
      {/* ========================================= */}
      <section ref={proofRef} className="py-24 relative overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
        {/* SVG Data Flow Background */}
        <div className="absolute left-0 top-0 bottom-0 w-1/2 opacity-10 pointer-events-none hidden lg:block">
          <svg className="w-full h-full" viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
            {/* Circuit lines */}
            <path d="M50,100 L150,100 L150,200 L250,200 L250,300 L350,300" fill="none" stroke="#6c5ce7" strokeWidth="1" opacity="0.5" />
            <path d="M100,50 L100,150 L200,150 L200,250 L300,250 L300,350" fill="none" stroke="#00cec9" strokeWidth="1" opacity="0.5" />
            <path d="M30,300 L130,300 L130,400 L230,400 L230,500 L330,500" fill="none" stroke="#6c5ce7" strokeWidth="1" opacity="0.3" />

            {/* Nodes */}
            <circle cx="150" cy="100" r="5" fill="#6c5ce7" opacity="0.8" />
            <circle cx="250" cy="200" r="5" fill="#00cec9" opacity="0.8" />
            <circle cx="350" cy="300" r="5" fill="#6c5ce7" opacity="0.8" />
            <circle cx="200" cy="150" r="4" fill="#00cec9" opacity="0.6" />
            <circle cx="300" cy="250" r="4" fill="#6c5ce7" opacity="0.6" />
            <circle cx="130" cy="400" r="5" fill="#fd79a8" opacity="0.5" />
            <circle cx="230" cy="500" r="5" fill="#00cec9" opacity="0.5" />

            {/* Glowing circles */}
            <circle cx="200" cy="300" r="60" fill="none" stroke="#6c5ce7" strokeWidth="0.5" opacity="0.3" />
            <circle cx="200" cy="300" r="90" fill="none" stroke="#00cec9" strokeWidth="0.5" opacity="0.2" />
            <circle cx="200" cy="300" r="120" fill="none" stroke="#6c5ce7" strokeWidth="0.5" opacity="0.1" />
          </svg>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Visual (Hidden on mobile, shown on desktop) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={proofInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="hidden lg:flex justify-center items-center"
            >
              <div className="relative w-80 h-80">
                {/* Animated rings */}
                <div className="absolute inset-0 rounded-full border border-[#6c5ce7]/30 animate-pulse"></div>
                <div className="absolute inset-4 rounded-full border border-[#00cec9]/20"></div>
                <div className="absolute inset-8 rounded-full border border-[#6c5ce7]/10"></div>

                {/* Center Glow */}
                <div className="absolute inset-12 rounded-full bg-gradient-to-br from-[#6c5ce7]/20 to-[#00cec9]/20 blur-xl"></div>

                {/* Center Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="p-8 rounded-full bg-gradient-to-br from-[#6c5ce7] to-[#00cec9]" style={{ boxShadow: '0 0 60px rgba(0, 206, 201, 0.5)' }}>
                    <MapPin size={48} className="text-white" />
                  </div>
                </div>

                {/* Orbiting dots */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#00cec9]" style={{ boxShadow: '0 0 20px #00cec9' }}></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#6c5ce7]" style={{ boxShadow: '0 0 15px #6c5ce7' }}></div>
                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-3 h-3 rounded-full bg-[#fd79a8]" style={{ boxShadow: '0 0 15px #fd79a8' }}></div>
              </div>
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={proofInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
                Pourquoi les PME du 78
                <span className="block bg-gradient-to-r from-[#6c5ce7] to-[#00cec9] bg-clip-text text-transparent">nous choisissent</span>
              </h2>

              <div className="space-y-8">
                {proofPoints.map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={proofInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="flex items-start space-x-4 p-6 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                    style={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <div className="flex-shrink-0 p-2 rounded-lg" style={{ background: 'rgba(0, 206, 201, 0.1)' }}>
                      <CheckCircle size={28} className="text-[#00cec9]" style={{ filter: 'drop-shadow(0 0 8px #00cec9)' }} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{point.title}</h3>
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{point.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Maillage vers la Méthodologie */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={proofInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-10"
              >
                <Link
                  to="/methodologie"
                  className="inline-flex items-center text-[#ffffff] font-medium group transition-all"
                >
                  <span className="border-b border-[#00cec9] pb-1 group-hover:text-[#00cec9] transition-colors">Découvrir notre méthode secrète</span>
                  <ArrowRight size={20} className="ml-2 text-[#00cec9] group-hover:translate-x-2 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================================= */}
      {/* SECTION BLOG - Maillage Interne SEO */}
      {/* ========================================= */}
      <section ref={blogRef} className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#00cec9] rounded-full blur-[150px] opacity-10"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#6c5ce7] rounded-full blur-[150px] opacity-10"></div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={blogInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6"
          >
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Nos dernières ressources
                <span className="bg-gradient-to-r from-[#00cec9] to-[#6c5ce7] bg-clip-text text-transparent"> SEO et Numériques</span>
              </h2>
              <p className="text-xl max-w-2xl" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Astuces, tutos et guides pour dominer les résultats de recherche locaux dans les Yvelines.
              </p>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center text-[#00cec9] hover:text-white transition-colors duration-300 font-medium whitespace-nowrap"
            >
              <BookOpen size={20} className="mr-2" />
              Voir tous nos articles
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Lien 1 - Maillage vers l'artisan */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={blogInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.1 }}>
              <Link to="/blog/pourquoi-artisan-besoin-site-web" className="group rounded-2xl block h-full p-8 transition-all duration-300 hover:scale-[1.02]"
                style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <div className="text-xs font-semibold text-[#00cec9] mb-4 flex items-center"><Calendar size={14} className="mr-1" /> Stratégie Digitale</div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#00cec9] transition-colors leading-tight">Pourquoi un artisan du bâtiment a besoin d'un site web en 2026 ?</h3>
                <p className="text-white/70 line-clamp-3">Attirez des chantiers réguliers et rassurez vos clients. Découvrez pourquoi le bouche-à-oreille local dans le 78 ne suffit plus.</p>
              </Link>
            </motion.div>

            {/* Lien 2 - Maillage vers Local/SEO */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={blogInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }}>
              <Link to="/blog/etre-premier-google-maps-commerce-local" className="group rounded-2xl block h-full p-8 transition-all duration-300 hover:scale-[1.02]"
                style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <div className="text-xs font-semibold text-[#00b894] mb-4 flex items-center"><Calendar size={14} className="mr-1" /> SEO Local</div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#00b894] transition-colors leading-tight">Comment dominer Google Maps quand on est un commerce local ?</h3>
                <p className="text-white/70 line-clamp-3">Le guide SEO local : optimisation de Google Business Profile et gestion des avis pour doubler vos visites physiques.</p>
              </Link>
            </motion.div>

            {/* Lien 3 - Maillage vers Prix/Conversion */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={blogInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3 }}>
              <Link to="/blog/prix-site-internet-yvelines" className="group rounded-2xl block h-full p-8 transition-all duration-300 hover:scale-[1.02]"
                style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <div className="text-xs font-semibold text-[#fd79a8] mb-4 flex items-center"><Calendar size={14} className="mr-1" /> Budget & ROI</div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#fd79a8] transition-colors leading-tight">Prix d'un site internet : Le vrai coût en 2026</h3>
                <p className="text-white/70 line-clamp-3">Site vitrine ou e-commerce ? Découvrez les vrais prix pratiqués par les agences web et calculez votre ROI numérique.</p>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================================= */}
      {/* CITIES SECTION - Maillage local */}
      {/* ========================================= */}
      <section ref={citiesRef} className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={citiesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Présents dans tout le
              <span className="bg-gradient-to-r from-[#6c5ce7] to-[#00cec9] bg-clip-text text-transparent"> 78</span>
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Nous accompagnons les entreprises dans toutes les villes des Yvelines
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {cities.map((city, index) => (
              <motion.div
                key={city.slug}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={citiesInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Link
                  to={`/agence-web/${city.slug}`}
                  className="block p-6 rounded-lg text-center transition-all duration-300 hover:scale-105 group"
                  style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="text-lg font-semibold text-white mb-1 group-hover:text-[#00cec9] transition-colors">
                    {city.name}
                  </div>
                  <div className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    {city.zipCode}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================= */}
      {/* SECTION FAQ MASSIVE - SEO LEAD GENERATION */}
      {/* ========================================= */}
      <section className="py-24 relative overflow-hidden" style={{ background: 'rgba(0,0,0,0.4)' }}>
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#6c5ce7]/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#00cec9]/50 to-transparent"></div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-20">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}>
            <div className="text-center mb-16">
              <span className="text-[#00cec9] font-bold tracking-widest uppercase text-sm mb-4 block">Foire Aux Questions</span>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Tout savoir sur la <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6c5ce7] to-[#00cec9]">Création Digitale</span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                Avant de confier votre image de marque francilienne à notre agence, voici les réponses transparentes et franches aux interrogations les plus fréquentes des chefs d'entreprise que nous accompagnons.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 text-left">

              {/* FAQ 1 */}
              <div className="bg-white/5 border border-white/10 p-8 pt-10 rounded-3xl relative group hover:bg-[#00cec9]/5 hover:border-[#00cec9]/30 transition-all duration-500">
                <div className="absolute -top-6 left-8 bg-[#1e1e24] border border-[#00cec9]/30 text-[#00cec9] font-black text-3xl w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_0_20px_rgba(0,206,201,0.2)]">1</div>
                <h3 className="text-2xl font-bold text-white mb-4 mt-2 group-hover:text-[#00cec9] transition-colors leading-tight">Combien coûte réellement un site internet professionnel en 2026 ?</h3>
                <p className="text-white/70 text-lg leading-relaxed mb-4">
                  Dans le paysage actuel, la conception d'un site web par une agence sérieuse des Yvelines ne doit plus être vue comme une dépense, mais comme un actif financier (ROI). Un site web vitrine premium (design unique, intégration de maquettes pointues, rédaction optimisée et structure sécurisée React) débute généralement entre 2 500 € et 3 500 €.
                </p>
                <p className="text-white/70 text-lg leading-relaxed">
                  Dès lors qu'il s'agit d'une plateforme E-commerce complexe intégrant des paiements sécurisés (Stripe) et de la logistique, ou de portails intégrant de l'intelligence artificielle automatisée, le budget se situe plutôt entre 5 000 € et 15 000 €. Toutefois, un dispositif tarifaire clair comme notre "Simulateur de Devis" vous permet de calculer le juste prix de votre ambition technologique en quelques clics.
                </p>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white/5 border border-white/10 p-8 pt-10 rounded-3xl relative group hover:bg-[#6c5ce7]/5 hover:border-[#6c5ce7]/30 transition-all duration-500">
                <div className="absolute -top-6 left-8 bg-[#1e1e24] border border-[#6c5ce7]/30 text-[#6c5ce7] font-black text-3xl w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_0_20px_rgba(108,92,231,0.2)]">2</div>
                <h3 className="text-2xl font-bold text-white mb-4 mt-2 group-hover:text-[#6c5ce7] transition-colors leading-tight">Quelle est votre stratégie contre l'obsolescence et le piratage ?</h3>
                <p className="text-white/70 text-lg leading-relaxed mb-4">
                  La cyber-sécurité et l'obsolescence programmée sont les fléaux du web. En bannissant l'usage abusif de WordPress et de ses milliers de plugins instables qui causent 90% des piratages mondiaux, nous immunisons presque totalement votre entreprise.
                </p>
                <p className="text-white/70 text-lg leading-relaxed">
                  L'architecture "Headless" ou basée sur React que nous construisons sépare la base de données de l'interface visuelle. Si un attaquant tente d'infiltrer la plateforme, il ne trouve aucune porte réseau ouverte. De plus, nous garantissons des contrats de maintenance préventive stricts. Notre code est pérenne, scalable et restera fluide sur les navigateurs web pendant de nombreuses années.
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white/5 border border-white/10 p-8 pt-10 rounded-3xl relative group hover:bg-[#fd79a8]/5 hover:border-[#fd79a8]/30 transition-all duration-500">
                <div className="absolute -top-6 left-8 bg-[#1e1e24] border border-[#fd79a8]/30 text-[#fd79a8] font-black text-3xl w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_0_20px_rgba(253,121,168,0.2)]">3</div>
                <h3 className="text-2xl font-bold text-white mb-4 mt-2 group-hover:text-[#fd79a8] transition-colors leading-tight">Le Référencement (SEO) Local est-il indispensable pour mon activité ?</h3>
                <p className="text-white/70 text-lg leading-relaxed mb-4">
                  Absolument. Il est même vital. 80% des Français cherchent un service ou un produit sur Google Maps avant même de se déplacer physiquement ou de passer un appel. Si vous êtes un artisan (plombier, menuisier), un restaurateur ou un prestataire de services (avocat, expert-comptable), votre visibilité de proximité définit la viabilité de votre entreprise.
                </p>
                <p className="text-white/70 text-lg leading-relaxed">
                  En travaillant avec nous, vous bénéficiez d'une stratégie SEO locale chirurgicale. Nous chassons les mots-clés de "longue traîne" pertinents localisés sur l'Île-de-France, et nous créons des ponts algorithmiques puissants entre votre site, votre fiche d'établissement, et des dizaines de citations géolocalisées pour prouver votre incontestable autorité régionale à Google.
                </p>
              </div>

              {/* FAQ 4 */}
              <div className="bg-white/5 border border-white/10 p-8 pt-10 rounded-3xl relative group hover:bg-[#00cec9]/5 hover:border-[#00cec9]/30 transition-all duration-500">
                <div className="absolute -top-6 left-8 bg-[#1e1e24] border border-[#00b894]/30 text-[#00b894] font-black text-3xl w-14 h-14 flex items-center justify-center rounded-2xl shadow-[0_0_20px_rgba(0,206,201,0.2)]">4</div>
                <h3 className="text-2xl font-bold text-white mb-4 mt-2 group-hover:text-[#00b894] transition-colors leading-tight">Y a-t-il un accompagnement après la Livraison du Site Internet ?</h3>
                <p className="text-white/70 text-lg leading-relaxed mb-4">
                  Lancer le site web n'est que le début de la course. La méthodologie "WEB YOU FIRST" intègre cet après-lancement au centre de la stratégie. Avoir un site en ligne ne suffit pas, il faut le "piloter" avec des données analytiques concrètes.
                </p>
                <p className="text-white/70 text-lg leading-relaxed">
                  Nous agissons comme votre département marketing externalisé. Nous analysons les statistiques Google Analytics sans vous noyer de jargon (temps moyen passé sur les pages, d'où proviennent exactement vos appels). Nous injectons des campagnes d'acquisition immédiate via Google Ads pour amorcer la pompe commerciale, et nous effectuons des réunions de bilans stratégiques mensuelles ou trimestrielles pour pivoter et accélérer la capture de parts de marché.
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      {/* ========================================= */}
      {/* SECTION 3 : LE LEVIER D'ACTION (CTA Final) */}
      {/* ========================================= */}
      <section ref={ctaRef} className="py-24 pb-32 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="relative rounded-3xl p-12 md:p-20 text-center overflow-hidden"
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}
            >
              {/* Intense Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#6c5ce7]/30 via-[#0a0a0c] to-[#00cec9]/30"></div>

              {/* Animated Glow Orbs */}
              <div className="absolute top-10 left-10 w-64 h-64 rounded-full blur-[100px] opacity-40"
                style={{ background: 'radial-gradient(circle, #6c5ce7 0%, transparent 70%)' }}></div>
              <div className="absolute bottom-10 right-10 w-64 h-64 rounded-full blur-[100px] opacity-40"
                style={{ background: 'radial-gradient(circle, #00cec9 0%, transparent 70%)' }}></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-[150px] opacity-20"
                style={{ background: 'radial-gradient(circle, #fd79a8 0%, transparent 70%)' }}></div>

              {/* Content */}
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Arrêtez de perdre des clients
                  <span className="block bg-gradient-to-r from-[#fd79a8] via-[#6c5ce7] to-[#00cec9] bg-clip-text text-transparent">
                    avec un site obsolète
                  </span>
                </h2>

                <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  Obtenez votre plan d'attaque digital personnalisé et une estimation précise.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link
                    to="/simulateur"
                    className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-black transition-all duration-300 hover:scale-105 group relative overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #00cec9, #6c5ce7)',
                      borderRadius: '0px',
                      boxShadow: '0 0 40px rgba(0, 206, 201, 0.5), 0 0 80px rgba(108, 92, 231, 0.3)'
                    }}
                    data-testid="cta-final-simulateur"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                    <Rocket size={24} className="mr-3" />
                    <span>Lancer le Simulateur de Devis</span>
                    <ArrowRight size={24} className="ml-3 group-hover:translate-x-1 transition-transform" />
                  </Link>

                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-white transition-all duration-300 hover:bg-white hover:text-black group"
                    style={{
                      background: 'transparent',
                      border: '2px solid rgba(255, 255, 255, 0.5)',
                      borderRadius: '0px'
                    }}
                    data-testid="cta-final-contact"
                  >
                    <Phone size={24} className="mr-3" />
                    <span>Parler à un expert</span>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
